/**
 * Start a new season (screen 11). Opens only once every published grid is
 * final or void; the server's preview says whether it can start, what the
 * next games are ("Monday Night Football · 2027"), and whether it needs a
 * new game or new dates first. Members and settings always carry over,
 * squares only when asked, numbers are always drawn fresh, and the old
 * season is archived. One transaction id per open sheet.
 */
import {
  useSquaresSeasonStart,
  useSquaresSeasonStartPreview,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  Dialog,
  InlineBanner,
  Input,
  Skeleton,
  Switch,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  runSquaresMutation,
  useRefreshSquaresPoolLine,
} from "./squaresActions";
import { SquaresSummaryRow } from "./SquaresAddGridSheet";
import {
  SQUARES_ADD_GRID_CANCEL,
  SQUARES_CUSTOM_DATES_FROM,
  SQUARES_CUSTOM_DATES_TO,
  SQUARES_NEW_SEASON_ARCHIVED,
  SQUARES_NEW_SEASON_BLOCKED,
  SQUARES_NEW_SEASON_CARRIES,
  SQUARES_NEW_SEASON_CARRY_SQUARES,
  SQUARES_NEW_SEASON_CARRY_SQUARES_DESCRIPTION,
  SQUARES_NEW_SEASON_MEMBERS,
  SQUARES_NEW_SEASON_NEEDS_DATES,
  SQUARES_NEW_SEASON_NEEDS_GAME,
  SQUARES_NEW_SEASON_NEXT_GAMES,
  SQUARES_NEW_SEASON_NO_SEASON,
  SQUARES_NEW_SEASON_NUMBERS,
  SQUARES_NEW_SEASON_NUMBERS_VALUE,
  SQUARES_NEW_SEASON_SETTINGS,
  SQUARES_NEW_SEASON_SETTINGS_VALUE,
  SQUARES_NEW_SEASON_SUBMIT,
  SQUARES_NEW_SEASON_TITLE,
  squaresNewSeasonArchivedValue,
  squaresNewSeasonMembersValue,
} from "./squaresCopy";
import { squaresTransactionId } from "./squaresModel";
import { SquaresOneGamePicker } from "./SquaresOneGamePicker";
import type { typeSquaresSeason } from "./squaresTypes";
import type { typeSquaresTeam } from "./useSquaresLeagueTeams";

export type typeSquaresNewSeasonSheetProps = {
  open: boolean;
  onClose: () => void;
  poolId: string;
  season: typeSquaresSeason;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
};

export const SquaresNewSeasonSheet = (
  props: typeSquaresNewSeasonSheetProps,
): ReactNode => {
  const refreshPoolLine = useRefreshSquaresPoolLine(props.poolId);
  const [doCarrySquares, setCarrySquares] = useState(false);
  const [eventId, setEventId] = useState<string | null>(null);
  const [fromDay, setFromDay] = useState("");
  const [toDay, setToDay] = useState("");
  const [transactionId, setTransactionId] = useState(squaresTransactionId);
  const [isBusy, setBusy] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    setCarrySquares(false);
    setEventId(null);
    setFromDay("");
    setToDay("");
    setTransactionId(squaresTransactionId());
  }, [props.open]);

  const previewQuery = useSquaresSeasonStartPreview(
    { poolId: props.poolId },
    { enabled: props.open },
  );
  const preview = previewQuery.data?.data ?? null;
  const seasonStart = useSquaresSeasonStart();

  const needsInput = preview?.needsInput ?? "none";
  const hasInput =
    needsInput === "none" ||
    (needsInput === "eventId" && eventId !== null) ||
    (needsInput === "customDates" && fromDay !== "" && toDay !== "");
  const isReady = preview !== null && preview.canStart && hasInput;

  async function submit(): Promise<void> {
    if (!isReady) return;
    setBusy(true);
    const result = await runSquaresMutation(
      () =>
        seasonStart.mutateAsync({
          poolId: props.poolId,
          doCarrySquares,
          ...(needsInput === "eventId" ? { eventId } : {}),
          ...(needsInput === "customDates"
            ? { customDates: { mode: "range" as const, fromDay, toDay } }
            : {}),
          transactionId,
        }),
      refreshPoolLine,
    );
    setBusy(false);
    if (result !== null) props.onClose();
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={SQUARES_NEW_SEASON_TITLE}
      description={preview?.currentSeasonLabel}
      autoFocus={false}
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {SQUARES_ADD_GRID_CANCEL}
          </Button>
          <Button
            disabled={!isReady}
            loading={isBusy}
            data-testid="squares-new-season-submit"
            onClick={() => {
              void submit();
            }}
          >
            {SQUARES_NEW_SEASON_SUBMIT}
          </Button>
        </>
      }
    >
      {preview === null ? (
        <div
          className="flex flex-col gap-2"
          data-testid="squares-new-season-loading"
        >
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : (
        <div
          className="flex flex-col gap-4"
          data-testid="squares-new-season-sheet"
        >
          {preview.canStart ? null : (
            <InlineBanner
              variant="warning"
              message={
                preview.blockedReason === "noActiveSeason"
                  ? SQUARES_NEW_SEASON_NO_SEASON
                  : SQUARES_NEW_SEASON_BLOCKED
              }
            />
          )}
          <section className="flex flex-col gap-2">
            <h3 className="text-body-strong text-ink">
              {SQUARES_NEW_SEASON_NEXT_GAMES}
            </h3>
            {preview.nextGamesLabel === null ||
            preview.nextGamesLabel === undefined ? null : (
              <p
                className="text-body text-ink"
                data-testid="squares-new-season-games"
              >
                {preview.nextGamesLabel}
              </p>
            )}
            {needsInput === "eventId" ? (
              <>
                <p className="text-caption text-ink-secondary">
                  {SQUARES_NEW_SEASON_NEEDS_GAME}
                </p>
                <SquaresOneGamePicker
                  leagueKey={props.season.leagueKey}
                  gridType={props.season.gridType}
                  selectedEventId={eventId}
                  onSelect={setEventId}
                  teamsById={props.teamsById}
                />
              </>
            ) : null}
            {needsInput === "customDates" ? (
              <>
                <p className="text-caption text-ink-secondary">
                  {SQUARES_NEW_SEASON_NEEDS_DATES}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={SQUARES_CUSTOM_DATES_FROM}
                    type="date"
                    value={fromDay}
                    data-testid="squares-new-season-from"
                    onChange={(event) => {
                      setFromDay(event.target.value);
                      if (toDay === "") setToDay(event.target.value);
                    }}
                  />
                  <Input
                    label={SQUARES_CUSTOM_DATES_TO}
                    type="date"
                    value={toDay}
                    min={fromDay === "" ? undefined : fromDay}
                    data-testid="squares-new-season-to"
                    onChange={(event) => {
                      setToDay(event.target.value);
                    }}
                  />
                </div>
              </>
            ) : null}
          </section>
          <section className="flex flex-col gap-1">
            <h3 className="text-body-strong text-ink">
              {SQUARES_NEW_SEASON_CARRIES}
            </h3>
            <dl className="flex flex-col divide-y divide-hairline">
              <SquaresSummaryRow
                label={SQUARES_NEW_SEASON_MEMBERS}
                value={squaresNewSeasonMembersValue(preview.memberCount)}
              />
              <SquaresSummaryRow
                label={SQUARES_NEW_SEASON_SETTINGS}
                value={SQUARES_NEW_SEASON_SETTINGS_VALUE}
              />
              <SquaresSummaryRow
                label={SQUARES_NEW_SEASON_NUMBERS}
                value={SQUARES_NEW_SEASON_NUMBERS_VALUE}
              />
            </dl>
            <Switch
              label={SQUARES_NEW_SEASON_CARRY_SQUARES}
              description={SQUARES_NEW_SEASON_CARRY_SQUARES_DESCRIPTION}
              checked={doCarrySquares}
              onCheckedChange={setCarrySquares}
            />
          </section>
          <section className="flex flex-col gap-1">
            <h3 className="text-body-strong text-ink">
              {SQUARES_NEW_SEASON_ARCHIVED}
            </h3>
            <p className="text-body text-ink-secondary">
              {squaresNewSeasonArchivedValue({
                gridCount: preview.archivedGridCount,
                winnerCount: preview.archivedWinnerCount,
              })}
            </p>
          </section>
        </div>
      )}
    </Dialog>
  );
};
